import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DocumentExt, ICoreAuthorizationDTO, IDocumentResponseDTO, MoveDocument, UploadDocument, UploadFile, UploadLetter, CalendarDay } from '@app/core/model';
import { DocumentsContextService } from '@app/core/services/context/factory';
import { ShellService } from '@mynexus/mfe-core';
import { Observable, of, switchMap } from 'rxjs';

@Injectable()
export class SharedDocumentsService implements DocumentsContextService {
  protected readonly API_URL;

  constructor(
    protected http: HttpClient,
    protected shellService: ShellService,
    @Inject('stream') private readonly stream: string
  ) {
    this.API_URL = shellService.getBaseUrlFor(stream);
  }

  //Public methods
  downloadZip(tenantId: string, authorizationByProductId: string, docMetaDataIds: string[]): Observable<Blob> {
    return this.http.post(`${this.getBaseUrl(tenantId, authorizationByProductId)}/DownloadZip`, docMetaDataIds, { responseType:'blob' });
  }

  getFileById(tenantId: string, authorizationByProductId: string, id: string): Observable<DocumentExt> {
    return this.http.get<DocumentExt>(`${this.getBaseUrl(tenantId, authorizationByProductId)}/GetDocumentById/${id}`);
  }

  moveFiles(tenantId: string, authorization: ICoreAuthorizationDTO, data: MoveDocument[]): Observable<boolean> {
    return this.http.post<boolean>(`${this.getBaseUrl(tenantId, authorization.authorizationId)}/MoveDocuments`, data);
  }

  getFilesByName(tenantId: string, authorizationNumber: string, documentFileName: string): Observable<IDocumentResponseDTO[]> {
    const encodedFileName = encodeURIComponent(documentFileName);
    return this.http.get<IDocumentResponseDTO[]>(`${this.API_URL}/Document/GetDocumentsByName/${tenantId}/${authorizationNumber}?documentFileName=${encodedFileName}`);
  }

  updateDocumentCheck(tenantId: string, authorizationByProductId: string, docMetadataId: string, isUncheck = false) {
    return this.http.put<IDocumentResponseDTO>(
      `${this.getBaseUrl(tenantId, authorizationByProductId)}/${isUncheck ? 'UncheckDocument' : 'CheckDocument'}/${docMetadataId}`,
      null
    );
  }

  uploadFile(tenantId: string, authorizationByProductId: string, data: UploadFile[], isWidgetUpload = false): Observable<UploadFile[]> {
    const options = isWidgetUpload ? { params: new HttpParams().set('isWidgetUpload', isWidgetUpload) } : {};
    return this.http.post<UploadFile[]>(`${this.getBaseUrl(tenantId, authorizationByProductId)}`, data, options);
  }

  updateDocumentReceivedTime(tenantId: string, authorizationByProductId: string, document: IDocumentResponseDTO): Observable<IDocumentResponseDTO> {
    return this.http.put<IDocumentResponseDTO>(`${this.getBaseUrl(tenantId, authorizationByProductId)}/UpdateDocumentReceivedTime`, document);
  }

  // each child must override
  getFilesMetaData(tenantId: string, authorizationByProductId: string, authorizationNumber: string): Observable<IDocumentResponseDTO[]> {
    return of();
  }

  getCalendarDay(startdate: string, numberofdays: number, isbusinessdatesonly: boolean): Observable<CalendarDay> {
    const baseUrlHH = this.shellService.getBaseUrlFor('HH')
    const url = `${baseUrlHH}/Utilities/CalendarDay/ByDays/${startdate}/${numberofdays}/${isbusinessdatesonly}`;

    return this.http.get<CalendarDay>(url);
  }

  uploadLetter(tenantId: string, authorization: ICoreAuthorizationDTO, data: UploadLetter): Observable<UploadLetter> {
    // Passing isUrgent as a query param to reuse BaseFileDTO in the BE instead of creating a new one.
    return this.http.post<UploadLetter>(`${this.getBaseUrl(tenantId, authorization.authorizationId)}/UploadLetterTranslation?isUrgent=${data.isUrgent}`, data);
  }

  mapAttachedFiles(files: UploadLetter[], config: {}): UploadLetter[] {
    return files;
  }

  deleteDocumentForMoveDocument(
    tenantId: string,
    authorizationByProductId: string,
    authorizationNumber: string,
    docMetaDataId: string,
    documentFileName: string,
  ): Observable<UploadDocument[]> {
    return this.getFilesMetaData(tenantId, authorizationByProductId, authorizationNumber)
      .pipe(switchMap(res => {
        const documentId: string = res.find(elem => elem.documentFileName === documentFileName)?.docMetaDataId;

        if (documentId) {
          return this.http.delete<UploadDocument[]>(`${this.getBaseUrl(tenantId, authorizationByProductId)}/?documentId=${documentId}`);
        } else {
          return of();
        }
      }));
  }

  //Protected Methods
  protected getBaseUrl(tenantId: string, id: string): string {
    return `${this.API_URL}/tenants/${tenantId}/authorizations/${id}/Documents`;
  }
}
